/** Copies text to the clipboard and says "Copied" for three seconds, in words and an icon, announced politely. */

import { Check, Copy } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { toast } from './Toast';

const SHOWN_MS = 3000;

interface CopyButtonProps {
  text: string;
  label?: string;
  /** Shown in place of the copy icon while idle. */
  icon?: ReactNode;
  disabled?: boolean;
}

export function CopyButton({ text, label = 'Copy', icon = <Copy aria-hidden="true" />, disabled = false }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => setCopied(true),
      () => toast.error('Could not copy', 'Your browser did not allow access to the clipboard.'),
    );
  };
  return (
    <>
      <button type="button" className="btn btn-secondary" disabled={disabled} onClick={copy}>
        {copied ? <Check aria-hidden="true" /> : icon} {copied ? 'Copied' : label}
      </button>
      <span className="sr-only" role="status">
        {copied ? 'Copied' : ''}
      </span>
    </>
  );
}
